import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';

import showToast from '@/components/ui/Toast';

import Table from '@/components/admin/Table';
import MabarClientCard, { MabarClientEvent } from '@/components/admin/MabarClientCard';
import DropdownButton, { DropdownItem } from '@/components/ui/DropdownButton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/shadcn/alert-dialog';
import { mabarService, ParticipationEvent, PaginationMeta } from '@/services/mabarService';
import { openExternalUrl } from '@/utils/openExternalUrl';
import { isHttpUrl } from '@/utils/paymentUrl';
import { clientPaths } from '@/utils/clientPaths';
import { MabarCardSkeleton } from '@/components/client/skeleton/ClientSkeletons';
import { formatLocalEventTimeRange } from '@/utils/dateUtils';

// Tab types
type TabType = 'upcoming' | 'history' | 'host';

// Items per page
const ITEMS_PER_PAGE = 12;

// Helper function to format date
const formatDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('id-ID', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

// Helper function to format time range
const formatTimeRange = (startTime: string, endTime: string): string => {
  return formatLocalEventTimeRange(startTime, endTime);
};

// History card: date block (day + month) from event
const getHistoryDateBlock = (event: MabarClientEvent): { day: string; month: string } => {
  const raw = event.eventDate || event.date;
  const d = raw.includes('-')
    ? new Date(raw)
    : (() => {
        const [dd, mm, yyyy] = raw.split('/');
        return new Date(Number(yyyy), Number(mm) - 1, Number(dd));
      })();
  const months = [
    'JAN',
    'FEB',
    'MAR',
    'APR',
    'MEI',
    'JUN',
    'JUL',
    'AGU',
    'SEP',
    'OKT',
    'NOV',
    'DES',
  ];
  return {
    day: d.getDate().toString(),
    month: months[d.getMonth()] || '',
  };
};

// History card: long date string (e.g. "12 September 2025")
const formatHistoryDateLong = (event: MabarClientEvent): string => {
  const raw = event.eventDate || event.date;
  const d = raw.includes('-')
    ? new Date(raw)
    : (() => {
        const [dd, mm, yyyy] = raw.split('/');
        return new Date(Number(yyyy), Number(mm) - 1, Number(dd));
      })();
  return d.toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });
};

// History card: status badge label
const getHistoryStatusLabel = (status: string): string => {
  const labels: Record<string, string> = {
    completed: 'Selesai',
    cancelled: 'Dibatalkan',
    ongoing: 'Berlangsung',
    published: 'Published',
    draft: 'Draft',
    plan: 'Rencana',
    ended: 'Selesai',
  };
  return (
    labels[status?.toLowerCase()] ||
    (status ? status.charAt(0).toUpperCase() + status.slice(1) : '')
  );
};

// History card: apakah tanggal event sudah lewat (untuk badge "Selesai")
const isEventDatePassed = (event: MabarClientEvent): boolean => {
  const raw = event.eventDate || event.date;
  const d = raw.includes('-')
    ? new Date(raw)
    : (() => {
        const [dd, mm, yyyy] = raw.split('/');
        return new Date(Number(yyyy), Number(mm) - 1, Number(dd));
      })();
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  return d < today;
};

// Helper function to group history events by month and year
interface HistoryMonthGroup {
  monthKey: string;
  label: string;
  countLabel: string;
  events: MabarClientEvent[];
}

const groupHistoryEventsByMonth = (eventsList: MabarClientEvent[]): HistoryMonthGroup[] => {
  const groupsMap = new Map<string, { label: string; events: MabarClientEvent[] }>();

  eventsList.forEach((event) => {
    const raw = event.eventDate || event.date;
    const d = raw.includes('-')
      ? new Date(raw)
      : (() => {
          const [dd, mm, yyyy] = raw.split('/');
          return new Date(Number(yyyy), Number(mm) - 1, Number(dd));
        })();

    const key = isNaN(d.getTime())
      ? 'lainnya'
      : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = isNaN(d.getTime())
      ? 'Riwayat Lainnya'
      : d.toLocaleDateString('id-ID', { month: 'long', year: 'numeric' });

    if (!groupsMap.has(key)) {
      groupsMap.set(key, { label, events: [] });
    }
    groupsMap.get(key)!.events.push(event);
  });

  return Array.from(groupsMap.entries()).map(([monthKey, group]) => ({
    monthKey,
    label: group.label,
    countLabel: `${group.events.length} Event`,
    events: group.events,
  }));
};

// Helper function to map API event to card event
const mapApiEventToCardEvent = (event: ParticipationEvent): MabarClientEvent => ({
  slug: event.slug,
  title: event.name,
  date: formatDate(event.event_date),
  time: formatTimeRange(event.start_time, event.end_time),
  venue: `${event.lokasi_arena} - ${event.city}`,
  sportType: event.sport_category.name,
  sportSlug: event.sport_category.slug,
  currentParticipants: event.filled_slots,
  maxParticipants: event.total_slots,
  isHost: event.is_host,
  isPrivate: event.visibility === 'private',
  status: event.status,
  price: event.type === 'free' ? 'free' : event?.my_price,
  isParticipant: event?.my_role != null && event?.my_role != '',
  linkGroup: event.link_group,
  participantStatus: event.my_status,
  autoConfirm: event.auto_confirm,
  eventDate: event.event_date,
  paymentStatus: event.my_payment_status,
  invoiceUrl: event.my_invoice_url,
  paymentMethod: event.my_payment_method,
  orderNumber: event.my_order_number,
  hostName: (event as any).host?.name || (event as any).host_name || 'Komunitas Sporto',
  hostAvatar: (event as any).host?.avatar || (event as any).host_avatar,
  mapLink: event.map_link,
});

// Sport type options for filter
const sportOptions = [
  { value: '', label: 'Semua Olahraga' },
  { value: 'Futsal', label: 'Futsal' },
  { value: 'Volley', label: 'Volley' },
  { value: 'Badminton', label: 'Badminton' },
  { value: 'Basketball', label: 'Basketball' },
  { value: 'Tennis', label: 'Tennis' },
];

// Status options for filter
const statusOptions = [
  { value: '', label: 'Semua Status' },
  { value: 'draft', label: 'Draft' },
  { value: 'published', label: 'Published' },
  { value: 'ongoing', label: 'Ongoing' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

// Tab button component (Defined outside to prevent unmounting re-renders)
const TabButton = ({
  tab,
  activeTab,
  icon,
  label,
  onClick,
}: {
  tab: TabType;
  activeTab: TabType;
  icon: string;
  label: string;
  onClick: (tab: TabType) => void;
}) => (
  <button
    type="button"
    onClick={() => onClick(tab)}
    className={`flex-1 sm:flex-none flex items-center justify-center gap-2 px-5 py-3 text-sm font-semibold border-b-2 transition-all cursor-pointer ${
      activeTab === tab
        ? 'border-sporto-orange text-sporto-orange'
        : 'border-transparent text-sporto-subtext hover:text-sporto-dark'
    }`}
  >
    <Icon icon={icon} className="w-4 h-4" />
    <span>{label}</span>
  </button>
);

const ClientMabarPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('upcoming');
  const [searchQuery, setSearchQuery] = useState('');
  const [sportFilter, setSportFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState<MabarClientEvent[]>([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [cancelingEvent, setCancelingEvent] = useState<MabarClientEvent | null>(null);
  const [cancelingLoading, setCancelingLoading] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedDetailEvent, setSelectedDetailEvent] = useState<MabarClientEvent | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);

  const loadEvents = async () => {
    setLoading(true);
    try {
      let response: { data: ParticipationEvent[]; meta: PaginationMeta };

      if (activeTab === 'host') {
        response = await mabarService.getMyHostedEvents(currentPage, ITEMS_PER_PAGE);
      } else {
        // upcoming or history
        response = await mabarService.getMyParticipations(
          activeTab as 'upcoming' | 'history',
          currentPage,
          ITEMS_PER_PAGE
        );
      }

      const mappedEvents = response.data.map(mapApiEventToCardEvent);
      setEvents(mappedEvents);
      setTotalItems(response.meta.total);
    } catch (error) {
      console.error('Failed to load events:', error);
      setEvents([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  // Load events from API
  useEffect(() => {
    loadEvents();
  }, [activeTab, currentPage]);

  // Reset page when tab changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab]);

  // Filter events based on search and filters (client-side filtering)
  const filteredEvents = events.filter((event) => {
    const matchesSearch = event.title.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesSport = !sportFilter || event.sportType === sportFilter;
    const matchesStatus = !statusFilter || event.status === statusFilter;
    return matchesSearch && matchesSport && matchesStatus;
  });

  // Event handlers
  const handleViewDetail = (event: MabarClientEvent) => {
    setSelectedDetailEvent(event);
    setIsDetailModalOpen(true);
  };

  const handleEdit = (event: MabarClientEvent) => {
    navigate(`/event/edit/${event.slug}`);
  };

  const handleShare = (event: MabarClientEvent) => {
    const url = `${window.location.origin}/event/${event.slug}`;
    navigator.clipboard.writeText(url);
    showToast.success('Link event berhasil disalin ke clipboard');
  };

  // Cancel event (host) - keep using window.confirm for deletion
  const handleCancel = async (event: MabarClientEvent) => {
    const confirmMessage = `Apakah Anda yakin ingin menghapus event "${event.title}"? Tindakan ini tidak dapat dibatalkan.`;

    if (!confirm(confirmMessage)) {
      return;
    }

    try {
      let result: { success: boolean; message?: string };

      result = await mabarService.deleteEvent(event.slug);

      if (result.success) {
        showToast.success('Event berhasil dihapus');
        setEvents((prev) => prev.filter((e) => e.slug !== event.slug));
      } else {
        showToast.error(result.message || 'Gagal menghapus event');
      }
    } catch (error: any) {
      console.error('Failed to delete event:', error);
      showToast.error(error.message || 'Gagal menghapus event');
    }
  };

  // Open confirm dialog for leaving an event (participant)
  const handleLeave = (event: MabarClientEvent) => {
    setCancelingEvent(event);
    setConfirmOpen(true);
  };

  // Perform leave after user confirms
  const performLeave = async () => {
    if (!cancelingEvent) return;
    setCancelingLoading(true);
    try {
      const result: { success: boolean; message?: string } = await mabarService.leaveEvent(
        cancelingEvent.slug
      );

      if (result.success) {
        showToast.success('Berhasil keluar event');
        // reload events to reflect change
        await loadEvents();
      } else {
        showToast.error(result.message || 'Gagal keluar event');
      }
    } catch (error: any) {
      console.error('Failed to leave event:', error);
      showToast.error(error.message || 'Gagal keluar event');
    } finally {
      setCancelingLoading(false);
      setConfirmOpen(false);
      setCancelingEvent(null);
    }
  };

  const handleCreateEvent = () => {
    navigate('/event/create');
  };

  const handlePay = async (event: MabarClientEvent) => {
    if (!event.orderNumber) {
      showToast.error('Nomor order tidak ditemukan');
      return;
    }

    const toastId = showToast.loading('Memuat halaman pembayaran...');
    try {
      const data = await mabarService.getTransactionCheckout(event.orderNumber);

      if (data?.success) {
        showToast.dismiss(toastId);
        if (data.payload) {
          navigate(clientPaths.event.checkout(event.orderNumber), {
            state: {
              paymentInstruction: data.payload,
              invoiceNumber: event.orderNumber,
              returnUrl: '/event',
              eventName: event.title,
              amount: typeof event.price === 'number' ? event.price : 0,
            },
          });
        } else if (isHttpUrl(data.invoice_url)) {
          void openExternalUrl(data.invoice_url);
        } else {
          navigate(clientPaths.event.checkout(event.orderNumber), {
            state: {
              invoiceNumber: event.orderNumber,
              returnUrl: '/event',
              eventName: event.title,
              amount: typeof event.price === 'number' ? event.price : 0,
            },
          });
        }
      } else {
        showToast.dismiss(toastId);
        showToast.error('Gagal memuat pembayaran');
      }
    } catch (err: any) {
      showToast.dismiss(toastId);
      console.error('Failed to checkout:', err);
      showToast.error(err.message || 'Gagal memuat pembayaran');
    }
  };

  // Pagination handlers
  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Generate page numbers for pagination
  const getPageNumbers = (): (number | string)[] => {
    const pages: (number | string)[] = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }

    return pages;
  };

  // Table columns for Host tab
  const hostColumns = [
    { key: 'title', title: 'Judul Event', width: 'w-80' },
    { key: 'date', title: 'Tanggal', width: 'w-40' },
    { key: 'venue', title: 'Venue', width: 'w-80' },
    { key: 'sportType', title: 'Sport Type', width: 'w-48' },
    { key: 'status', title: 'Status', width: 'w-40' },
    { key: 'type', title: 'Type', width: 'w-40' },
    { key: 'slotTerisi', title: 'Slot Terisi', width: 'w-40' },
  ];

  const handleViewDashboard = (event: MabarClientEvent) => {
    navigate(`/event/dashboard/${event.slug}`);
  };

  // Dropdown menu for host table actions
  const getHostDropdownMenu = (row: MabarClientEvent): DropdownItem[] => [
    // {
    //   label: 'Dashboard',
    //   icon: 'mdi:view-dashboard-outline',
    //   onClick: () => handleViewDashboard(row),
    // },
    {
      label: 'Edit',
      icon: 'mdi:pencil-outline',
      onClick: () => handleEdit(row),
    },
    {
      label: 'Share link Event',
      icon: 'mdi:share-variant-outline',
      onClick: () => handleShare(row),
    },
    {
      label: 'Cancel Event',
      icon: 'mdi:close',
      onClick: () => handleCancel(row),
      destructive: true,
    },
  ];

  // Format price display
  const formatPrice = (price?: number | 'free') => {
    if (price === undefined || price === 'free' || price === 0) return 'Gratis';
    return `Rp. ${price.toLocaleString('id-ID')}`;
  };

  // Get sport icon
  const getSportIcon = (sportType: string): string => {
    const icons: Record<string, string> = {
      futsal: 'mdi:soccer',
      soccer: 'mdi:soccer',
      volley: 'mdi:volleyball',
      volleyball: 'mdi:volleyball',
      badminton: 'mdi:badminton',
      basketball: 'mdi:basketball',
      tennis: 'mdi:tennis',
    };
    return icons[sportType.toLowerCase()] || 'mdi:soccer';
  };

  // Render status badge
  const renderStatusBadge = (status: string) => {
    const statusStyles: Record<string, string> = {
      plan: 'text-primary-500',
      ended: 'text-gray-500',
      cancelled: 'text-red-500',
    };

    return (
      <span className={`font-medium capitalize ${statusStyles[status] || 'text-gray-500'}`}>
        {status === 'plan' ? 'Plan' : status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  return (
    <div className="bg-sporto-bg text-sporto-dark pb-12">
      {/* Mobile Sticky Tabs (Flush with Primary Orange Header) */}
      <div className="sticky top-0 z-20 bg-white border-b border-sporto-border shadow-xs md:hidden">
        {/* Mobile Tab Navigation (Flush with Top Header) */}
        <div className="flex items-center border-t border-sporto-border/60 px-2">
          <TabButton tab="upcoming" activeTab={activeTab} onClick={setActiveTab} icon="ph:calendar-dots" label="Upcoming" />
          <TabButton tab="history" activeTab={activeTab} onClick={setActiveTab} icon="ph:clock-counter-clockwise" label="Riwayat" />
          {/* Hide Host sementara */}
          {/* <TabButton tab="host" activeTab={activeTab} onClick={setActiveTab} icon="ph:crown" label="Host" /> */}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 md:px-6 pt-4 md:pt-6">
        {/* Desktop Header */}
        <div className="hidden md:block mb-6">
          <h1 className="text-2xl font-bold text-sporto-dark">Event Mabar</h1>
          <p className="text-xs text-sporto-subtext mt-0.5">Kelola event mabar yang kamu ikuti atau host</p>
        </div>

        {/* Desktop Tab Navigation (Card Container Style) */}
        <div className="hidden md:flex items-center border-b border-[#E0E6ED] bg-[#FFFFFF] rounded-2xl px-6 mb-6 shadow-[inset_0_0_0_1px_#E0E6ED]">
          <TabButton tab="upcoming" activeTab={activeTab} onClick={setActiveTab} icon="ph:calendar-dots" label="Upcoming" />
          <TabButton tab="history" activeTab={activeTab} onClick={setActiveTab} icon="ph:clock-counter-clockwise" label="Riwayat" />
          <TabButton tab="host" activeTab={activeTab} onClick={setActiveTab} icon="ph:crown" label="Host" />
        </div>

        {/* Search & Filter Controls */}
        <div className="flex items-center gap-3 mb-6">
          <div className="flex-1 relative h-11">
            <Icon
              icon="ph:magnifying-glass"
              className="w-5 h-5 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2"
            />
            <input
              type="text"
              placeholder="Cari event atau venue..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-full pl-10 pr-4 rounded-xl bg-white text-sm text-[#0E1726] placeholder:text-gray-400 border-none shadow-[inset_0_0_0_1px_#E0E6ED] focus:outline-none focus:shadow-[inset_0_0_0_1.5px_#FC480D] transition-all"
            />
          </div>
          <button
            onClick={() => setIsFilterOpen(!isFilterOpen)}
            className={`w-11 h-11 rounded-xl bg-white shadow-[inset_0_0_0_1px_#E0E6ED] flex items-center justify-center transition-colors shrink-0 cursor-pointer ${
              isFilterOpen ? 'shadow-[inset_0_0_0_1.5px_#FC480D] text-[#FC480D] bg-[#FFF2EE]' : 'text-[#0E1726] hover:bg-gray-50'
            }`}
          >
            <Icon icon="ph:sliders-horizontal" className="w-5 h-5" />
          </button>
        </div>

        {/* Collapsible Filter Panel (Design Improvement) */}
        {isFilterOpen && (
          <div className="bg-white rounded-2xl p-4 shadow-[inset_0_0_0_1px_#E0E6ED] mb-6 flex flex-col gap-4 animate-in fade-in slide-in-from-top-2 duration-200">
            <div className="flex flex-col gap-2">
              <span className="text-xs font-semibold text-[#556575]">Jenis Olahraga</span>
              <div className="flex flex-wrap gap-2">
                {sportOptions.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setSportFilter(opt.value)}
                    className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer ${
                      sportFilter === opt.value
                        ? 'bg-[#FFF2EE] text-[#FC480D] shadow-[inset_0_0_0_1px_#FC480D]'
                        : ' text-[#556575] shadow-[inset_0_0_0_1px_#E0E6ED] hover:bg-gray-200'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <span className="text-xs font-semibold text-[#556575]">Status Pembayaran / Event</span>
              <div className="flex flex-wrap gap-2">
                {statusOptions.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setStatusFilter(opt.value)}
                    className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer ${
                      statusFilter === opt.value
                        ? 'bg-[#FFF2EE] text-[#FC480D] shadow-[inset_0_0_0_1px_#FC480D]'
                        : ' text-[#556575] shadow-[inset_0_0_0_1px_#E0E6ED] hover:bg-gray-200'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between pt-2 border-t border-[#E0E6ED]">
              <button
                onClick={() => {
                  setSportFilter('');
                  setStatusFilter('');
                  setSearchQuery('');
                }}
                className="text-xs font-semibold text-[#556575] hover:text-[#0E1726] cursor-pointer"
              >
                Reset Filter
              </button>
              <button
                onClick={() => setIsFilterOpen(false)}
                className="text-xs font-bold text-[#FC480D] hover:underline cursor-pointer"
              >
                Terapkan
              </button>
            </div>
          </div>
        )}

        {/* Content Section: Smooth In-place Skeleton Loading or Tab Contents */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pt-2">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <MabarCardSkeleton key={i} />
            ))}
          </div>
        ) : (
          <div className="animate-in fade-in duration-150 space-y-4">
            {/* UPCOMING TAB */}
            {activeTab === 'upcoming' && (
          <div className="space-y-4">
            {/* Tab Count Info for Upcoming */}
            <div className="text-xs font-medium text-[#556575]">
              {filteredEvents.length} event akan datang
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredEvents.length === 0 ? (
                <div className="col-span-full bg-white rounded-2xl p-10 text-center shadow-[inset_0_0_0_1px_#E0E6ED]">
                  <Icon icon="ph:calendar-x" className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <h3 className="text-base font-bold text-[#0E1726] mb-1">Tidak Ada Event Upcoming</h3>
                  <p className="text-xs text-[#556575] mb-4">Belum ada event mabar yang akan datang saat ini.</p>
                  <button
                    onClick={() => navigate('/discovery/event')}
                    className="px-5 py-2.5 rounded-full bg-[#FC480D] text-white text-xs font-semibold hover:bg-[#D83B08] transition-colors cursor-pointer shadow-xs"
                  >
                    Cari Event
                  </button>
                </div>
              ) : (
                filteredEvents.map((event) => (
                  <MabarClientCard
                    key={event.slug}
                    event={event}
                    onViewDetail={handleViewDetail}
                    onViewDashboard={handleViewDashboard}
                    onEdit={handleEdit}
                    onShare={handleShare}
                    onCancel={handleCancel}
                    onLeave={handleLeave}
                    onPay={handlePay}
                  />
                ))
              )}
            </div>
          </div>
        )}

        {/* HISTORY TAB */}
        {activeTab === 'history' && (
          <div className="space-y-4">
            {filteredEvents.length === 0 ? (
              <div className="bg-white rounded-2xl p-10 text-center shadow-[inset_0_0_0_1px_#E0E6ED]">
                <Icon icon="ph:clock-counter-clockwise" className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <h3 className="text-base font-bold text-[#0E1726] mb-1">Tidak Ada Riwayat Event</h3>
                <p className="text-xs text-[#556575] mb-4">Belum ada riwayat event mabar yang selesai atau kamu ikuti.</p>
                <button
                  onClick={() => navigate('/discovery/event')}
                  className="px-5 py-2.5 rounded-full bg-[#FC480D] text-white text-xs font-semibold hover:bg-[#D83B08] transition-colors cursor-pointer shadow-xs"
                >
                  Cari Event
                </button>
              </div>
            ) : (
              groupHistoryEventsByMonth(filteredEvents).map((group) => (
                <div key={group.monthKey} className="space-y-3 mb-6">
                  {/* Month Divider Bar */}
                  <div className="flex items-center gap-2.5 pt-2">
                    <span className="text-xs font-bold text-[#556575] shrink-0 capitalize">{group.label}</span>
                    <div className="flex-1 h-px bg-[#E0E6ED]" />
                    <span className="text-xs font-medium text-[#556575] shrink-0">{group.countLabel}</span>
                  </div>

                  {group.events.map((event) => {
                    const { day, month } = getHistoryDateBlock(event);
                    const isCancelled = event.status?.toLowerCase() === 'cancelled';
                    const datePassed = isEventDatePassed(event);
                    const statusLabel = isCancelled
                      ? 'Dibatalkan'
                      : datePassed
                        ? 'Selesai'
                        : getHistoryStatusLabel(event.status);
                    const badgeBg = isCancelled ? 'bg-red-50 text-red-600' : datePassed ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-700';
                    const priceText = formatPrice(event.price);
                    const isUnpaidQris =
                      !isCancelled &&
                      event.price !== 'free' &&
                      event.price !== undefined &&
                      typeof event.price === 'number' &&
                      event.price > 0 &&
                      event.paymentStatus?.toLowerCase() === 'unpaid' &&
                      event.paymentMethod?.toLowerCase() === 'qris';

                    return (
                      <div
                        key={event.slug}
                        onClick={() => handleViewDetail(event)}
                        className="bg-white rounded-2xl p-4 sm:p-5 shadow-[inset_0_0_0_1px_#E0E6ED] flex flex-col gap-3.5 cursor-pointer hover:shadow-xs transition-shadow"
                      >
                        {/* Top Row: Sport Badge & Status Badge */}
                        <div className="flex items-center justify-between gap-2">
                          <span className="inline-flex items-center gap-1.5 bg-[#FFF2EE] text-[#FC480D] px-2.5 py-1 rounded-full text-xs font-semibold">
                            <Icon icon={getSportIcon(event.sportType)} className="w-3.5 h-3.5" />
                            {event.sportType}
                          </span>
                          <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${badgeBg}`}>
                            {statusLabel}
                          </span>
                        </div>

                        {/* Middle Content Row */}
                        <div className="flex gap-3.5 items-start">
                          {/* Date Block */}
                          <div className="w-14 sm:w-16 h-20 rounded-xl  flex flex-col items-center justify-center shrink-0 border border-[#E0E6ED]">
                            <span className="text-[11px] font-bold text-[#FC480D] uppercase">{month}</span>
                            <span className="text-2xl font-bold text-[#0E1726] leading-none my-0.5">{day}</span>
                          </div>

                          {/* Event Details */}
                          <div className="flex-1 min-w-0 flex flex-col gap-1.5">
                            <h3 className="text-base font-bold text-[#0E1726] truncate">{event.title}</h3>
                            <div className="flex items-center gap-2 text-xs text-[#556575]">
                              <Icon icon="ph:calendar-blank" className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                              <span>{formatHistoryDateLong(event)}</span>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-[#556575] min-w-0">
                              <Icon icon="ph:map-pin" className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                              <span className="truncate">{event.venue}</span>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-[#556575]">
                              <Icon icon="ph:users-three" className="w-3.5 h-3.5 text-[#FC480D] shrink-0" />
                              <span className="font-medium text-[#0E1726]">{priceText}</span>
                            </div>
                          </div>
                        </div>

                        {/* Bottom Row: Actions */}
                        <div className="flex items-center justify-between gap-3 pt-3 border-t border-[#E0E6ED]">
                          <span className="text-xs text-[#556575] truncate">
                            {isUnpaidQris ? 'Belum dibayar (QRIS)' : 'Terdaftar'}
                          </span>
                          <div className="flex items-center gap-2 shrink-0">
                            {/* Tombol Bayar QRIS disembunyikan sementara.
                            {isUnpaidQris && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handlePay(event);
                                }}
                                className="h-8 px-3.5 rounded-full bg-[#FC480D] text-white text-xs font-semibold hover:bg-[#D83B08] transition-colors flex items-center gap-1.5 cursor-pointer shadow-xs"
                              >
                                <Icon icon="ph:qr-code" className="w-3.5 h-3.5" />
                                Bayar
                              </button>
                            )}
                            */}
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewDetail(event);
                              }}
                              className="h-8 px-3 rounded-full bg-white text-[#0E1726] shadow-[inset_0_0_0_1px_#E0E6ED] hover:border-[#FC480D] hover:text-[#FC480D] text-xs font-semibold transition-all flex items-center gap-1 cursor-pointer"
                            >
                              Detail
                              <Icon icon="ph:caret-right" className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))
            )}
          </div>
        )}

        {/* HOST TAB */}
        {activeTab === 'host' && (
          <div className="space-y-4">
            {filteredEvents.length === 0 ? (
              <div className="bg-white rounded-2xl p-10 text-center shadow-[inset_0_0_0_1px_#E0E6ED]">
                <Icon icon="ph:crown" className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <h3 className="text-base font-bold text-[#0E1726] mb-1">Belum Ada Event yang Di-host</h3>
                <p className="text-xs text-[#556575] mb-4">Kamu belum pernah membuat event mabar sebagai host.</p>
                <button
                  onClick={handleCreateEvent}
                  className="px-5 py-2.5 rounded-full bg-[#FC480D] text-white text-xs font-semibold hover:bg-[#D83B08] transition-colors cursor-pointer shadow-xs"
                >
                  Buat Event Mabar
                </button>
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-[inset_0_0_0_1px_#E0E6ED] overflow-hidden">
                <Table
                  columns={hostColumns}
                  data={filteredEvents.map((e) => ({
                    ...e,
                    type: e.isPrivate ? 'Private' : 'Public',
                    slotTerisi: `${e.currentParticipants}/${e.maxParticipants} (${Math.round((e.currentParticipants / e.maxParticipants) * 100)}%)`,
                  }))}
                  renderCell={(row, key) => {
                    if (key === 'sportType') {
                      return (
                        <div className="flex items-center gap-1.5 text-[#FC480D] font-semibold text-xs">
                          <Icon icon={getSportIcon(row.sportType)} className="w-4 h-4" />
                          {row.sportType}
                        </div>
                      );
                    }
                    if (key === 'status') {
                      return renderStatusBadge(row.status);
                    }
                    return row[key];
                  }}
                  renderActions={(row) => (
                    <div className="flex items-center gap-2">
                      <DropdownButton
                        items={getHostDropdownMenu(row)}
                        triggerVariant="ghost"
                        triggerSize="sm"
                        triggerClassName="px-1 py-1"
                      />
                    </div>
                  )}
                />
              </div>
            )}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-2 mt-8">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="p-2 rounded-xl bg-white shadow-[inset_0_0_0_1px_#E0E6ED] hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            >
              <Icon icon="ph:caret-left" className="w-5 h-5" />
            </button>
            {getPageNumbers().map((page, idx) => (
              <button
                key={idx}
                onClick={() => typeof page === 'number' && handlePageChange(page)}
                disabled={page === '...'}
                className={`min-w-[40px] h-10 px-3 rounded-xl text-xs font-bold transition-all ${
                  page === currentPage
                    ? 'bg-[#FC480D] text-white shadow-xs'
                    : page === '...'
                      ? 'cursor-default text-gray-400'
                      : 'bg-white text-[#0E1726] shadow-[inset_0_0_0_1px_#E0E6ED] hover:bg-gray-100 cursor-pointer'
                }`}
              >
                {page}
              </button>
            ))}
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="p-2 rounded-xl bg-white shadow-[inset_0_0_0_1px_#E0E6ED] hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            >
              <Icon icon="ph:caret-right" className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>
    )}
      </div>

      {/* AlertDialog for Leaving Event */}
      <AlertDialog
        open={confirmOpen}
        onOpenChange={(open) => {
          if (!open) {
            setConfirmOpen(false);
            setCancelingEvent(null);
          }
        }}
      >
        <AlertDialogContent className="max-w-md rounded-2xl p-6">
          <AlertDialogHeader>
            <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <Icon icon="ph:sign-out-bold" className="w-7 h-7 text-red-500" />
            </div>
            <AlertDialogTitle className="text-center text-lg font-bold text-[#0E1726]">Keluar Event Mabar</AlertDialogTitle>
            <AlertDialogDescription className="text-center text-xs text-[#556575]">
              {cancelingEvent ? (
                <span>
                  Kamu akan mengajukan permintaan keluar dari event{' '}
                  <strong className="text-[#0E1726]">"{cancelingEvent.title}"</strong>. Permintaan ini memerlukan konfirmasi host.
                </span>
              ) : (
                'Apakah Anda yakin ingin keluar dari event ini?'
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex gap-2.5 sm:gap-3 mt-2">
            <AlertDialogCancel disabled={cancelingLoading} className="flex-1 rounded-full h-11 border-[#E0E6ED] text-xs font-semibold cursor-pointer">
              Batal
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={performLeave}
              disabled={cancelingLoading}
              className="flex-1 rounded-full h-11 bg-red-500 hover:bg-red-600 text-white text-xs font-semibold cursor-pointer"
            >
              {cancelingLoading ? (
                <>
                  <Icon icon="ph:spinner-gap-bold" className="w-4 h-4 animate-spin mr-1.5" />
                  Memproses...
                </>
              ) : (
                'Keluar Event'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bottom Sheet Action Popup Modal (Design Improvement) */}
      {isDetailModalOpen && selectedDetailEvent && (
        <div
          className="fixed inset-0 z-50 bg-[#0E1726]/60 backdrop-blur-xs flex items-end justify-center animate-in fade-in duration-200"
          onClick={() => {
            setIsDetailModalOpen(false);
            setSelectedDetailEvent(null);
          }}
        >
          <div
            className="w-full max-w-md bg-white rounded-t-3xl p-5 sm:p-6 flex flex-col gap-4 shadow-2xl animate-in slide-in-from-bottom duration-300"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Drag handle bar */}
            <div className="w-12 h-1 rounded-full bg-gray-300 self-center -mt-1 mb-1" />

            {/* Event Title */}
            <h3 className="text-lg sm:text-xl font-bold text-[#0E1726] leading-snug">
              {selectedDetailEvent.title}
            </h3>

            {/* Event Info Metadata List */}
            <div className="flex flex-col gap-2.5 text-xs text-[#556575] font-medium">
              {/* Date & Time */}
              <div className="flex items-center gap-2.5">
                <Icon icon="ph:calendar-blank-bold" className="w-4 h-4 text-[#FC480D] shrink-0" />
                <span>
                  {formatHistoryDateLong(selectedDetailEvent)} · {selectedDetailEvent.time}
                </span>
              </div>

              {/* Venue / Location */}
              <div className="flex items-center gap-2.5 min-w-0">
                <Icon icon="ph:map-pin-bold" className="w-4 h-4 text-sporto-orange shrink-0" />
                <span className="truncate">{selectedDetailEvent.venue}</span>
              </div>

              {/* Participants Count */}
              <div className="flex items-center gap-2.5">
                <Icon icon="ph:users-three-bold" className="w-4 h-4 text-sporto-orange shrink-0" />
                <span>
                  {selectedDetailEvent.currentParticipants}/{selectedDetailEvent.maxParticipants} Bergabung
                </span>
              </div>

              {/* Price & Payment Status */}
              <div className="flex items-center gap-2.5">
                <Icon icon="ph:ticket-bold" className="w-4 h-4 text-sporto-orange shrink-0" />
                <span className="font-semibold text-sporto-dark">
                  {formatPrice(selectedDetailEvent.price)}
                  {selectedDetailEvent.paymentStatus ? (
                    <span className="ml-1.5 text-emerald-600 font-bold uppercase">
                      · {selectedDetailEvent.paymentStatus}
                    </span>
                  ) : null}
                </span>
              </div>
            </div>

            {/* Host Card */}
            <div className="bg-sporto-bg rounded-2xl p-3.5 flex items-center gap-3 border border-sporto-border my-1">
              <div className="w-10 h-10 rounded-full bg-sporto-orange-light border border-orange-200 text-sporto-orange font-bold text-sm flex items-center justify-center overflow-hidden shrink-0">
                {selectedDetailEvent.hostAvatar ? (
                  <img
                    src={selectedDetailEvent.hostAvatar}
                    alt={selectedDetailEvent.hostName || 'Host'}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  (selectedDetailEvent.hostName || 'Sporto Host').charAt(0).toUpperCase()
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-bold text-sporto-dark truncate">
                  {selectedDetailEvent.hostName || 'Komunitas Sporto'}
                </p>
                <p className="text-[11px] text-sporto-subtext">Host Event Mabar</p>
              </div>
            </div>

            {/* Action Buttons Row */}
            <div className="flex items-center gap-3 pt-1">
              {/* Outlined Maps Button */}
              <button
                type="button"
                onClick={() => {
                  const mapsUrl =
                    selectedDetailEvent.mapLink ||
                    `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(selectedDetailEvent.venue)}`;
                  void openExternalUrl(mapsUrl);
                  setIsDetailModalOpen(false);
                }}
                className="flex-1 h-12 rounded-full border-2 border-sporto-orange text-sporto-orange bg-white font-bold text-sm flex items-center justify-center gap-2 hover:bg-sporto-orange-light active:scale-98 transition-all cursor-pointer shadow-xs"
              >
                <Icon icon="ph:navigation-arrow" className="w-4.5 h-4.5 text-sporto-orange" />
                Maps
              </button>

              {/* Solid Orange Statistik Button */}
              <button
                type="button"
                onClick={() => {
                  navigate(`/event/${selectedDetailEvent.slug}/statistik`);
                  setIsDetailModalOpen(false);
                }}
                className="flex-1 h-12 rounded-full bg-sporto-orange hover:bg-sporto-orange-hover active:scale-98 text-white font-bold text-sm flex items-center justify-center gap-2 transition-all cursor-pointer shadow-xs"
              >
                <Icon icon="ph:chart-bar" className="w-4.5 h-4.5 text-white" />
                Statistik
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientMabarPage;
